import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LogOut, Settings, User, Globe, Bell, Shield, BarChart3, BookOpen, Eye } from 'lucide-react';

export default function Header() {
  const { user, logout, hasPermission } = useAuth();
  const { currentLanguage, availableLanguages, setLanguage, translate } = useLanguage();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLearnerRoute = location.pathname === '/' || location.pathname.startsWith('/training');

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to={user?.role === 'admin' || user?.role === 'manager' ? '/admin' : '/'} className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isAdminRoute ? 'DVSA Admin' : 'DVSA Training'}
                </h1>
                <p className="text-xs text-gray-500">
                  {isAdminRoute ? 'Management Portal' : 'Professional Development'}
                </p>
              </div>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* Role-based Navigation */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div className="flex items-center space-x-2">
                <Link
                  to="/admin"
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isAdminRoute 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
                <Link
                  to="/learner"
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname.startsWith('/learner')
                      ? 'bg-green-100 text-green-700' 
                      : 'text-gray-700 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  <span>Learner View</span>
                </Link>
              </div>
            )}

            {user?.role === 'trainer' && (
              <div className="flex items-center space-x-2">
                <Link
                  to="/"
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isLearnerRoute
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Learning</span>
                </Link>
                <Link
                  to="/admin"
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isAdminRoute 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Manage</span>
                </Link>
              </div>
            )}

            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Language Selector */}
            <div className="relative group">
              <button className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                <Globe className="w-4 h-4" />
                <span>{currentLanguage.flag} {currentLanguage.name}</span>
              </button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize flex items-center">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      user?.role === 'admin' ? 'bg-red-500' :
                      user?.role === 'manager' ? 'bg-orange-500' :
                      user?.role === 'trainer' ? 'bg-purple-500' : 'bg-blue-500'
                    }`}></span>
                    {user?.role}
                  </p>
                </div>
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {user?.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-2">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                      <div className="flex items-center mt-2">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                          user?.role === 'admin' ? 'bg-red-500' :
                          user?.role === 'manager' ? 'bg-orange-500' :
                          user?.role === 'trainer' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}></span>
                        <span className="text-xs text-gray-400 capitalize">
                          {user?.role} • {user?.department}
                        </span>
                      </div>
                    </div>
                    
                    <div className="py-2">
                      <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                        <Settings className="w-4 h-4" />
                        <span>Profile Settings</span>
                      </button>
                      
                      {/* Role-specific menu items */}
                      {(user?.role === 'admin' || user?.role === 'manager') && !isAdminRoute && (
                        <Link
                          to="/admin"
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Shield className="w-4 h-4" />
                          <span>Admin Dashboard</span>
                        </Link>
                      )}

                      {user?.role === 'trainer' && !isAdminRoute && (
                        <Link
                          to="/admin"
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <BarChart3 className="w-4 h-4" />
                          <span>Training Management</span>
                        </Link>
                      )}

                      {(user?.role === 'admin' || user?.role === 'manager') && isAdminRoute && (
                        <Link
                          to="/learner"
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Eye className="w-4 h-4" />
                          <span>View as Learner</span>
                        </Link>
                      )}
                      
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
}